/**
 * Presentation Page
 * Client-facing "old vs new" pitch page — reusable across every portal.
 * Content comes from portal.landingPage.presentation (set per-business via
 * seed/admin data); falls back to generic copy when a business hasn't set
 * its own yet. Linked from the main nav (2026-09-21) once a
 * presentationAccessKey has been entered - see PreviewKeyGate below, which
 * also gates the goals/pricing/payment section appended after the static
 * comparison shell above.
 */

import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { PreviewKeyGate, PresentationData, FeedbackWelcome } from 'shared-ui';
import { usePortal } from '../context/PortalContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getApiBaseUrl } from '../services/api';
import './PresentationPage.css';

const defaultContent = (businessName) => ({
  eyebrow: businessName,
  title: 'Your Site & System, Upgraded',
  subtitle: "A side-by-side look at what changes for your customers — and for you — moving from the current website to the new one.",
  features: {
    old: {
      label: 'Current Site & System',
      items: [
        { title: 'Manual enquiries', desc: 'Requests come in by phone, email, or a basic contact form with no structure.' },
        { title: 'No online pricing', desc: 'Every job needs back-and-forth before a number exists.' },
        { title: 'No order tracking', desc: "Job status lives in calls, texts, and someone's memory." },
        { title: 'Static branding', desc: 'Plain logo, no motion, same as every other trade site.' },
      ],
    },
    new: {
      label: 'New Site & System',
      items: [
        { title: 'Structured requests', desc: 'Everything needed to quote is captured upfront, per product or service.' },
        { title: 'Online quotes & approval', desc: 'You set a price once; the customer approves it online and it becomes an order automatically.' },
        { title: 'Live order tracking', desc: 'Every job tracked from request through to completion.' },
        { title: 'Animated, on-brand logo', desc: 'Your real brand colors, in motion, on every page.' },
      ],
    },
  },
  pipeline: {
    old: ['Customer calls / emails', 'Details chased manually', 'Quote by phone / email', 'No tracked status'],
    new: ['Request', 'Quote sent', 'Customer approves', 'Received', 'In progress', 'Ready', 'Completed'],
  },
  callout: {
    title: "The biggest change isn't visual",
    body: 'Every job now has a visible path from "asked" to "done" — for the first time.',
  },
});

const PresentationPage = () => {
  const { branding, portal, businessId, loading, hasPreviewAccess } = usePortal();
  const content = portal?.landingPage?.presentation || defaultContent(branding.businessName);
  const logoUrl = branding.logoUrl;

  return (
    <>
      <Header />

      <main style={{ marginTop: '80px' }}>
        {/* How to tell us what they think - only once they've entered their access code (2026-09-28). */}
        {hasPreviewAccess && <FeedbackWelcome onPresentation />}
        <div className="presentation-page">
          <div className="presentation-masthead">
            <div className="presentation-eyebrow">{content.eyebrow}</div>
            <h1>{content.title}</h1>
            <p>{content.subtitle}</p>
          </div>

          {logoUrl && (
            <div className="presentation-logo-showcase">
              <div className="presentation-logo-panel">
                <div className="presentation-logo-label">Before</div>
                <img className="presentation-logo-static" src={logoUrl} alt={`${branding.businessName} logo, current`} />
              </div>
              <div className="presentation-arrow" aria-hidden="true">&rarr;</div>
              <div className="presentation-logo-panel">
                <div className="presentation-logo-label">After</div>
                <span className="logo-flashy-wrap">
                  <img src={logoUrl} alt={`${branding.businessName} logo, animated`} />
                </span>
              </div>
            </div>
          )}

          <div className="presentation-section-heading">
            <h2>Old Site &amp; System vs. New Site &amp; System</h2>
            <p>Same brand. A completely different engine underneath it.</p>
          </div>

          <div className="presentation-compare">
            <div className="presentation-col presentation-col-old">
              <div className="presentation-col-title">{content.features.old.label}</div>
              {content.features.old.items.map((item) => (
                <div className="presentation-feature-row" key={item.title}>
                  <XCircle className="presentation-icon presentation-icon-old" size={20} strokeWidth={2} />
                  <div className="presentation-text">
                    <b>{item.title}</b>
                    <span>{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="presentation-divider" aria-hidden="true" />

            <div className="presentation-col presentation-col-new">
              <div className="presentation-col-title presentation-col-title-new">{content.features.new.label}</div>
              {content.features.new.items.map((item) => (
                <div className="presentation-feature-row" key={item.title}>
                  <CheckCircle2 className="presentation-icon presentation-icon-new" size={20} strokeWidth={2} />
                  <div className="presentation-text">
                    <b>{item.title}</b>
                    <span>{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="presentation-section-heading">
            <h2>What happens to a job, start to finish</h2>
            <p>The same customer enquiry, handled two different ways.</p>
          </div>

          <div className="presentation-timelines">
            <div className="presentation-timeline-block">
              <div className="presentation-col-title">Old — Manual, by hand</div>
              <div className="presentation-timeline presentation-timeline-old">
                {content.pipeline.old.map((step, i) => (
                  <div className="presentation-timeline-step" key={step}>
                    <div className="presentation-timeline-node">{i + 1}</div>
                    <div className="presentation-timeline-label">{step}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="presentation-timeline-block">
              <div className="presentation-col-title presentation-col-title-new">New — Structured, tracked</div>
              <div className="presentation-timeline presentation-timeline-new">
                {content.pipeline.new.map((step, i) => (
                  <div className="presentation-timeline-step" key={step}>
                    <div className="presentation-timeline-node">{i + 1}</div>
                    <div className="presentation-timeline-label">{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="presentation-callout">
            <h3>{content.callout.title}</h3>
            <p>{content.callout.body}</p>
          </div>
        </div>

        {/* Goals/pricing/ABN/payment (2026-09-21) - the actual proposal, pulled live from this
            prospect's Lead record. Gated the same way as the Dashboard tab - see shared-ui's
            PreviewKeyGate/PresentationData, which do the real work. */}
        {!loading && businessId && (
          <PreviewKeyGate apiBaseUrl={getApiBaseUrl()} businessId={businessId}>
            {(previewKey) => <PresentationData apiBaseUrl={getApiBaseUrl()} businessId={businessId} previewKey={previewKey} />}
          </PreviewKeyGate>
        )}
      </main>

      <Footer />
    </>
  );
};

export default PresentationPage;
